import ImageCompressWorker from 'worker-loader!./imageCompressWebpackWorker.js';

/**
 * Create a Webpack 4 compatible worker bundled by worker-loader.
 *
 * The worker keeps the package's JSquash encoders and WASM execution off the
 * main thread while avoiding the Webpack 5-only new URL(import.meta.url) path.
 *
 * @returns {Worker}
 */
export function createDefaultWorker() {
  return new ImageCompressWorker();
}

/**
 * Run one compression attempt and preserve the package worker-client contract.
 *
 * @param {File|Blob} file image input
 * @param {Object} options worker compression options
 * @param {Object} [runtimeOptions={}] runtime options
 * @returns {Promise<Object>} worker result
 */
export function runCompressionWorker(file, options, runtimeOptions = {}) {
  var createWorker = runtimeOptions.createWorker || createDefaultWorker;
  var timeout = runtimeOptions.timeout;
  var signal = runtimeOptions.signal;
  var consoleOptions = runtimeOptions.consoleOptions;

  // 压缩开始前已经中断时，不再创建 worker。
  if (signal && signal.aborted) {
    writeCompressConsole(consoleOptions, '压缩开始前已中断，跳过创建 worker');
    return Promise.reject(createAbortError());
  }

  var worker = createWorker();

  // worker 创建失败时直接返回错误。
  if (!worker) {
    return Promise.reject(
      new Error('Unable to create image compression worker')
    );
  }

  return new Promise(function (resolve, reject) {
    var settled = false;
    var preprocessFallbackResult = null;
    var timer = globalThis.setTimeout(function () {
      writeCompressConsole(consoleOptions, '压缩超时，已终止 worker', {
        timeout: timeout
      });
      // 已拿到尺寸处理结果时，超时只跳过真正压缩，继续使用预处理结果。
      if (preprocessFallbackResult) {
        settle(
          resolve,
          createPreprocessFallbackResult(preprocessFallbackResult, 'timeout')
        );
        return;
      }
      settle(reject, new Error('Image compression timed out'));
    }, timeout);

    var abortHandler = function () {
      writeCompressConsole(consoleOptions, '压缩收到中断信号，已终止 worker');
      // 已拿到尺寸处理结果时，中断只跳过真正压缩，继续使用预处理结果。
      if (preprocessFallbackResult) {
        settle(
          resolve,
          createPreprocessFallbackResult(preprocessFallbackResult, 'abort')
        );
        return;
      }
      settle(reject, createAbortError());
    };

    /**
     * 结束 worker 压缩任务。
     * @param {Function} done resolve 或 reject。
     * @param {*} value 返回值或错误。
     * @returns {void}
     */
    function settle(done, value) {
      // 已经结束的任务不再重复触发回调。
      if (settled) return;

      settled = true;
      cleanupWorker();
      done(value);
    }

    /**
     * 清理 worker、定时器和中断监听。
     * @returns {void}
     */
    function cleanupWorker() {
      globalThis.clearTimeout(timer);

      // 清理外部中断监听，避免任务结束后继续持有回调。
      if (signal) {
        signal.removeEventListener('abort', abortHandler);
      }

      worker.onmessage = null;
      worker.onerror = null;
      worker.terminate();
      preprocessFallbackResult = null;
      writeCompressConsole(consoleOptions, '压缩 worker 已释放');
    }

    // 外部中断时立刻终止 worker，避免后台继续压缩。
    if (signal) {
      signal.addEventListener('abort', abortHandler, { once: true });
    }

    worker.onmessage = function (event) {
      // worker 在真正编码前会先回传尺寸处理结果，供失败或超时时兜底。
      if (event.data && event.data.type === 'preprocessFallback') {
        preprocessFallbackResult = event.data.result;
        writeCompressConsole(consoleOptions, '已收到压缩前尺寸处理兜底结果', {
          width: preprocessFallbackResult && preprocessFallbackResult.width,
          height: preprocessFallbackResult && preprocessFallbackResult.height,
          size: preprocessFallbackResult && preprocessFallbackResult.encodedSize
        });
        return;
      }

      // worker 成功时返回压缩结果。
      if (event.data && event.data.ok) {
        // 大像素保护命中时记录原因，方便线上定位是否跳过了真正压缩。
        if (event.data.compressionFallback && event.data.fallbackReason) {
          writeCompressConsole(consoleOptions, '压缩走兜底结果', {
            fallbackReason: event.data.fallbackReason,
            width: event.data.width,
            height: event.data.height,
            pixels: event.data.pixels,
            maxJsEncodePixels: event.data.maxJsEncodePixels,
            encoder: event.data.encoder,
            size: event.data.encodedSize
          });
        }
        settle(resolve, event.data);
        return;
      }

      // 已拿到尺寸处理结果时，压缩失败只跳过真正压缩。
      if (preprocessFallbackResult) {
        settle(
          resolve,
          createPreprocessFallbackResult(preprocessFallbackResult, 'error')
        );
        return;
      }

      settle(
        reject,
        new Error(
          (event.data && event.data.error) || 'Image compression worker failed'
        )
      );
    };

    worker.onerror = function (event) {
      // 已拿到尺寸处理结果时，worker 报错只跳过真正压缩。
      if (preprocessFallbackResult) {
        settle(
          resolve,
          createPreprocessFallbackResult(
            preprocessFallbackResult,
            'workerError'
          )
        );
        return;
      }
      settle(
        reject,
        new Error((event && event.message) || 'Image compression worker failed')
      );
    };

    worker.postMessage({ file: file, options: options });
  });
}

/**
 * 创建压缩中断错误。
 * @returns {Error} AbortError。
 */
function createAbortError() {
  var error = new Error('Image compression aborted');
  error.name = 'AbortError';
  return error;
}

/**
 * 标记压缩前尺寸处理兜底结果的原因。
 * @param {Object} fallbackResult worker 回传的预处理结果。
 * @param {string} reason 使用兜底结果的原因。
 * @returns {Object} 带原因的压缩结果。
 */
function createPreprocessFallbackResult(fallbackResult, reason) {
  return Object.assign({}, fallbackResult, {
    compressionFallback: true,
    fallbackReason: reason
  });
}

/**
 * 输出压缩调试日志。
 * @param {Object|null} consoleOptions 压缩调试日志配置。
 * @param {string} title 日志标题。
 * @param {Object} data 日志数据。
 * @returns {void}
 */
function writeCompressConsole(consoleOptions, title, data) {
  data = data || {};
  // 未传入调试配置时保持静默。
  if (!consoleOptions || typeof consoleOptions.console !== 'function') return;

  consoleOptions.console({
    page: consoleOptions.page || 'utils/lib/imageCompressWorkerClientCompat',
    method: consoleOptions.method || 'runCompressionWorker',
    title: title,
    data: Object.assign({}, consoleOptions.data || {}, data)
  });
}
